// src/features/records/components/GalleryView/components/MediaThumbnail.js
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PlayCircle, VideoOff } from 'lucide-react';
import { useThumbUrls } from '../../../hooks/useThumbUrls';
import styles from './MediaThumbnail.module.css';

/**
 * MediaThumbnail
 *  - Responsive cell that renders an image or a video poster
 *  - Shows skeleton → image → error fallback
 *  - Video gets a play icon + duration
 */
const MediaThumbnail = ({ media, style, onClick }) => {
  const { t } = useTranslation();
  const { src, srcSet, loading, error, onImgErrorOnce } = useThumbUrls(media);
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgRef = useRef(null);

  // Reset load state whenever the URL changes so fade-in runs again.
  useEffect(() => {
    setImgLoaded(false);
  }, [src]);

  // Handle cached images that complete before onLoad fires.
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setImgLoaded(true);
    }
  }, [src]);

  const hasSrc = Boolean(src);
  const shouldRenderImg = hasSrc && !error;

  // Skeleton appears while URLs resolve or while the image is still painting.
  const showSkeleton = !shouldRenderImg || (shouldRenderImg && !imgLoaded);

  // Error surfaces only after we've attempted to resolve a usable URL.
  const showError = !loading && (!hasSrc || !!error);

  return (
    <button
      data-media-id={media.id}
      type="button"
      className={styles.wrapper}
      style={style}
      onClick={(e) => onClick?.(media, e)}
      aria-label={
        media.type === 'video'
          ? t('records.views.gallery.actions.play_video_aria')
          : t('records.views.gallery.actions.view_image_aria')
      }
      aria-busy={showSkeleton ? 'true' : undefined}
      data-state={showError ? 'error' : showSkeleton ? 'loading' : 'ready'}
    >
      {/* Skeleton layer to avoid broken image flash */}
      {!showError && showSkeleton && (
        <div className={`${styles.skeleton} ${styles.skeletonLayer}`} aria-hidden="true" />
      )}

      {/* Image / Poster — only mount when a real src exists */}
      {shouldRenderImg && (
        <img
          key={src}
          ref={imgRef}
          src={src}
          srcSet={srcSet}
          sizes={`${style?.width || 160}px`} // Grid cell size hint
          alt=""
          loading="lazy"
          decoding="async"
          className={`${styles.thumbImg} ${imgLoaded ? styles.visible : styles.hidden}`}
          onLoad={() => setImgLoaded(true)}
          onError={onImgErrorOnce}
        />
      )}

      {/* Error placeholder (only rendered after attempting resolution) */}
      {showError && (
        <div className={styles.errorBox}>
          {media.type === 'video' ? <VideoOff /> : '✕'}
        </div>
      )}

      {/* Video overlay (layout stable) */}
      {media.type === 'video' && (
        <div className={styles.videoOverlay}>
          <PlayCircle className={styles.playIcon} />
          {media.duration && (
            <span className={styles.duration}>
              {formatDuration(media.duration)}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

MediaThumbnail.propTypes = {
  media: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['image', 'video']).isRequired,
    thumbnails: PropTypes.shape({
      '400': PropTypes.string,
      '800': PropTypes.string,
      poster: PropTypes.string,
    }),
    duration: PropTypes.number,
    storagePath: PropTypes.string,
    thumbnailUrl: PropTypes.string, // Optional pre-signed URL
    posterUrl: PropTypes.string, // Optional pre-signed URL
  }).isRequired,
  style: PropTypes.object,
  onClick: PropTypes.func,
};

function formatDuration(totalSeconds = 0) {
  const m = Math.floor(totalSeconds / 60).toString();
  const s = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}

export default MediaThumbnail;
